/**
 * Hook to fetch and manage user's generated videos from database
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserVideos } from "@/services/db/generatedVideos";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import type { GenerationStatus } from "@shared/schemas/enums";
import { useCompletionAnimation } from "@/hooks/useCompletionAnimation";

export interface GeneratedVideo {
  id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  reference_image_url: string | null;
  end_frame_url: string | null;
  prompt: string;
  model: string;
  duration: number;
  resolution: string;
  aspect_ratio: string;
  sound_enabled: boolean;
  status: GenerationStatus;
  task_id: string | null;
  error_message: string | null;
  credit_cost: number;
  created_at: string;
  completed_at: string | null;
  pipeline: string | null;
}

export function useGeneratedVideos(pipeline?: string) {
  const { user } = useAuth();
  const [videos, setVideos] = useState<GeneratedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVideos = useCallback(async () => {
    if (!user) {
      setVideos([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await fetchUserVideos(user.id);
      setVideos(data as GeneratedVideo[]);
      setError(null);
    } catch (err) {
      console.error("Error fetching generated videos:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch videos");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Subscribe to realtime updates
  useRealtimeSync<GeneratedVideo>({
    channelName: "videos-gallery-updates",
    table: "generated_videos",
    filter: `user_id=eq.${user?.id}`,
    enabled: !!user,
    onInsert: (video) => {
      setVideos((prev) => [video, ...prev]);
    },
    onUpdate: (video) => {
      setVideos((prev) => prev.map((v) => (v.id === video.id ? video : v)));
    },
    onDelete: (old) => {
      setVideos((prev) => prev.filter((v) => v.id !== old.id));
    },
  });

  // Filter by pipeline when provided
  const filteredVideos = pipeline
    ? videos.filter((v) => v.pipeline === pipeline)
    : videos;

  // Get pending/processing videos
  const rawPendingVideos = filteredVideos.filter(
    (v) => v.status === "pending" || v.status === "processing"
  );

  const getVideoById = useCallback(
    (id: string) => filteredVideos.find((v) => v.id === id),
    [filteredVideos]
  );

  const { completingIds, pendingItems: pendingVideos } = useCompletionAnimation({
    rawPendingItems: rawPendingVideos,
    getItemById: getVideoById,
  });

  // Get only completed videos for display (exclude items in completion animation)
  const completedVideos = filteredVideos.filter(
    (v) => v.status === "completed" && !completingIds.has(v.id)
  );

  return {
    videos,
    completedVideos,
    pendingVideos,
    completingIds,
    loading,
    error,
    refetch: fetchVideos,
  };
}
